import { Space, Tag, Tooltip, Typography, message } from "antd";
import FilterMenu from "components/_storybooks/FilterMenu";

import TagFilterCustom from "components/_storybooks/TagFilterCustom";
import StateSwitch from "components/StateSwitch";
import { DATE_TIME_FORMAT6 } from "config/constants";
import {
  useActivateVoucher,
  useCountVouchers,
  useDeactivateVoucher,
  useGetVouchersCampaign,
  useShowVoucherCodes,
} from "hooks/campaign";
import useDebounce from "hooks/useDebounce";
import { Copy, Eye, EyeOff } from "lucide-react";
import { useEffect, useState } from "react";
import { formatDateTime } from "utils/dateTime";
import { formatCurrency, notify } from "utils/helperFuncs";
import { omit } from "utils/object";
import { FilterQuantity } from "../FilterProduct";

export default function useInfoVoucher({ promotionCampaignIDs }) {
  const [codeVisibility, setCodeVisibility] = useState({});
  const [voucherCodes, setVoucherCodes] = useState({}); // Lưu mã voucher đã fetch
  const [activeTab, setActiveTab] = useState("ALL");

  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  // State cho modal xem chi tiết đại lý
  const [agentModalVisible, setAgentModalVisible] = useState(false);
  const [selectedVoucherID, setSelectedVoucherID] = useState(null);

  // State cho modal xem chi tiết sản phẩm
  const [productModalVisible, setProductModalVisible] = useState(false);

  const { handleActivateVoucher, loading: loadingActivateVoucher } = useActivateVoucher();
  const { handleDeactivateVoucher, loading: loadingDeactivateVoucher } = useDeactivateVoucher();
  const { handleShowVoucherCodes } = useShowVoucherCodes();

  const [params, setParams] = useState({
    filters: {
      promotionCampaignIDs: [promotionCampaignIDs] || [],
      statuses: [],
    },
    pagination: {
      limit: 10,
      offset: 0,
    },
  });

  const { data, paginationData, loading, refetch } = useGetVouchersCampaign(params);

  const handleToggleCode = async (record) => {
    const id = record.id;
    const isCurrentlyVisible = codeVisibility[id] || false;

    if (isCurrentlyVisible) {
      // Đang hiện -> ẩn đi
      setCodeVisibility((prev) => ({ ...prev, [id]: false }));
    } else {
      // Đang ẩn -> fetch và hiện
      try {
        const res = await handleShowVoucherCodes({ voucherIDs: [id] });

        const code = res?.data?.promotionCampaign?.showVoucherCodes?.[0]?.voucherCode || "--";
        setVoucherCodes((prev) => ({ ...prev, [id]: code }));
        setCodeVisibility((prev) => ({ ...prev, [id]: true }));
      } catch (error) {
        message.error("Không thể hiển thị mã voucher");
      }
    }
  };

  // Map status sang text và màu sắc
  const getStatusConfig = (status) => {
    const statusMap = {
      UPCOMING: { text: "Sắp diễn ra", color: "#FAAD14" }, // Vàng cam
      ONGOING: { text: "Có hiệu lực", color: "#52C41A" }, // Xanh lá
      EXPIRED: { text: "Hết hiệu lực", color: "#FF4D4F" }, // Đỏ
      OUT_OF_USAGE: { text: "Hết lượt sử dụng", color: "#FF4D4F" }, // Đỏ
      DISABLE: { text: "Đã vô hiệu", color: "#8C8C8C" }, // Xám
    };
    return statusMap[status] || { text: status, color: "#8C8C8C" };
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    message.success("Đã sao chép mã vào bộ nhớ tạm");
  };

  function handleFilterListVoucher(keyFilter, value) {
    setParams({
      ...params,
      filters: {
        ...params.filters,
        [keyFilter]: value,
      },
      pagination: {
        ...params.pagination,
        offset: 0,
      },
    });
  }

  const tagsListNormal = [
    {
      title: "Trạng thái",
      options: [
        { label: "Sắp diễn ra", value: "UPCOMING" },
        { label: "Có hiệu lực", value: "ONGOING" },
        { label: "Hết hiệu lực", value: "EXPIRED" },
        { label: "Hết lượt sử dụng", value: "OUT_OF_USAGE" },
        { label: "Đã vô hiệu", value: "DISABLE" },
      ],
      selected: params?.filters?.statuses,
      key: "statuses",
    },
  ];

  const handleRemoveFilterTag = (key) => {
    setParams({ ...params, filters: omit(params?.filters, [key]), pagination: { offset: 0, limit: 10 } });
  };

  const renderTagsList = tagsListNormal
    ?.filter(({ selected }) => selected?.length > 0)
    ?.map(({ options, selected, title, key, loading }) => {
      return (
        <TagFilterCustom
          arrValue={selected}
          onClose={() => handleRemoveFilterTag(key)}
          loading={loading}
          title={title}
          arrTags={options}
          isTimeRange={true}
        />
      );
    });

  const renderTagUsedQuantity = (key) => {
    const renderRealStocks = (from, to) => {
      if (!from && to) {
        return `<=${to}`;
      }
      if (from && !to) {
        return `>=${from}`;
      }

      if (from && to) {
        return `${from} - ${to}`;
      }
      return "";
    };

    return (
      params.filters.usedQuantityRange && (
        <Tag closable onClose={() => handleRemoveFilterTag("usedQuantityRange")}>
          <>
            <span className="label">Số lượt đã dùng:</span>
            <span>
              &nbsp;{renderRealStocks(params.filters.usedQuantityRange?.from, params.filters.usedQuantityRange?.to)}
            </span>
          </>
        </Tag>
      )
    );
  };

  const columns = [
    {
      title: "STT",
      width: 60,
      align: "center",
      fixed: "left",
      render: (_, record, index) => {
        return <Typography.Text>{index + 1}</Typography.Text>;
      },
    },
    {
      title: "MÃ VOUCHER",
      dataIndex: "code",
      width: 280,
      fixed: "left",
      render: (_, record) => {
        const isVisible = codeVisibility[record.id] || false;
        const displayCode = voucherCodes[record.id] || "**********";

        return (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography.Link strong>{isVisible ? displayCode : "**********"}</Typography.Link>
            <Space>
              {isVisible ? (
                <Tooltip title="Ẩn mã voucher">
                  <EyeOff size={16} style={{ cursor: "pointer" }} onClick={() => handleToggleCode(record)} />
                </Tooltip>
              ) : (
                <Tooltip title="Xem mã voucher">
                  <Eye size={16} style={{ cursor: "pointer" }} onClick={() => handleToggleCode(record)} />
                </Tooltip>
              )}
              {isVisible && (
                <Tooltip title="Sao chép">
                  <Copy
                    size={16}
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      handleCopyCode(
                        `${voucherCodes[record.id] || record.code} - ${record?.promotionCampaignTier?.name || ""}`
                      )
                    }
                  />
                </Tooltip>
              )}
            </Space>
          </div>
        );
      },
    },
    {
      title: "MỨC ƯU ĐÃI",
      dataIndex: "discountLevel",
      width: 300,
      render: (_, record) => <Typography.Text>{record?.promotionCampaignTier?.name}</Typography.Text>,
    },
    {
      title: "SỐ LƯỢT ĐÃ DÙNG",
      dataIndex: "usedQuantity",
      align: "center",
      width: 200,
      render: (_, record) => {
        return <Typography.Text>{record?.usedQuantity || "- -"} lượt</Typography.Text>;
      },
      filterDropdown: (
        <FilterQuantity
          onFilter={(value) => {
            handleFilterListVoucher("usedQuantityRange", value.usedQuantityRange);
          }}
          defaultValue={params.filters.usedQuantityRange}
        />
      ),
    },
    {
      title: "KHÁCH HÀNG MỤC TIÊU",
      dataIndex: "targetCustomer",
      render: (_, record) => {
        const sellerUserSegmentType = record?.promotionCampaignTier?.sellerUserSegment?.type;
        const sellerAppliedNumber = record?.promotionCampaignTier?.sellerAppliedNumber;

        // Map type sang text hiển thị
        const customerTypeText = {
          ALL: "Tất cả đại lý",
          NEW_SELLER: "Đại lý mới",
          NOT_LOGGED_IN: "Đại lý chưa sử dụng app",
          NO_ORDER: "Đại lý chưa phát sinh đơn hàng",
          NOT_PURCHASED_PRODUCT: "Đại lý chưa từng mua các sản phẩm này",
          IN_GROUP: "Từ danh sách đại lý hiện có",
          HAVE_VOUCHER_CODE: "Đại lý có vouchers",
        };

        return (
          <div>
            <Typography.Text>{customerTypeText[sellerUserSegmentType] || "-"}</Typography.Text>
            {sellerUserSegmentType === "IN_GROUP" && sellerAppliedNumber > 0 && (
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Có {sellerAppliedNumber} đại lý |{" "}
                  <Typography.Link
                    style={{ fontSize: 12 }}
                    onClick={() => {
                      setSelectedVoucherID(record?.promotionCampaignTier?.id);
                      setAgentModalVisible(true);
                    }}
                  >
                    Xem chi tiết
                  </Typography.Link>
                </Typography.Text>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "SỐ LƯỢT SỬ DỤNG MỨC ƯU ĐÃI / ĐẠI LÝ",
      dataIndex: "usageLimit",
      align: "center",
      width: 200,
      render: (_, record) => {
        return (
          <Typography.Text>{record?.promotionCampaignTier?.maxQuantityPerSeller || "Không giới hạn"}</Typography.Text>
        );
      },
    },
    {
      title: "THỜI GIAN SỬ DỤNG MÃ",
      dataIndex: "usageTime",
      align: "center",
      render: (_, record) => {
        const timeOfUsed = record?.timeOfUsed;
        if (!timeOfUsed) return <Typography.Text>-</Typography.Text>;

        const startDate = timeOfUsed.startDate ? formatDateTime(timeOfUsed.startDate, DATE_TIME_FORMAT6) : "";
        const endDate = timeOfUsed.endDate ? formatDateTime(timeOfUsed.endDate, DATE_TIME_FORMAT6) : "";
        return <Typography.Text>{`${startDate || "- -"} - ${endDate || "- -"}`}</Typography.Text>;
      },
    },
    {
      title: "THỜI GIAN HIỆU LỰC MÃ",
      dataIndex: "validityPeriod",
      align: "center",
      render: (_, record) => {
        const effectivePeriod = record?.effectivePeriod;
        if (!effectivePeriod) return <Typography.Text>- -</Typography.Text>;

        if (effectivePeriod.isSpecificTime) {
          const startAt = effectivePeriod.startAt ? formatDateTime(effectivePeriod.startAt, DATE_TIME_FORMAT6) : "";
          const endAt = effectivePeriod.endAt ? formatDateTime(effectivePeriod.endAt, DATE_TIME_FORMAT6) : "";
          return <Typography.Text>{`${startAt || "- -"} - ${endAt || "- -"}`}</Typography.Text>;
        }

        const durationUnitText = {
          DAY: "ngày",
          WEEK: "tuần",
          MONTH: "tháng",
          YEAR: "năm",
        };
        const duration = effectivePeriod.duration || 0;
        const unit = durationUnitText[effectivePeriod.durationUnit] || "";
        return <Typography.Text>{duration > 0 ? `${duration || 0} ${unit || "tháng"}` : "- -"}</Typography.Text>;
      },
    },
    {
      title: "SẢN PHẨM ÁP DỤNG",
      dataIndex: "applicableProducts",
      render: (_, record) => {
        const conditionProductGroups = record?.promotionCampaignTier?.conditionProductGroups;
        const productAppliedNumber = record?.promotionCampaignTier?.productAppliedNumber;

        if (!conditionProductGroups || conditionProductGroups.length === 0) {
          return <Typography.Text>Tất cả sản phẩm</Typography.Text>;
        }

        const productType = conditionProductGroups[0]?.conditionProductType;
        const productTypeText = {
          INCLUDED: "Sản phẩm cụ thể",
          EXCLUDED: "Không bao gồm sản phẩm đã chọn",
        };

        return (
          <div>
            <Typography.Text>{productTypeText[productType] || "Tất cả sản phẩm"}</Typography.Text>
            {productAppliedNumber > 0 && (
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Có {productAppliedNumber} sản phẩm |{" "}
                  <Typography.Link
                    style={{ fontSize: 12 }}
                    onClick={() => {
                      setSelectedVoucherID(record?.promotionCampaignTier?.id);
                      setProductModalVisible(true);
                    }}
                  >
                    Xem chi tiết
                  </Typography.Link>
                </Typography.Text>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "NGÂN SÁCH",
      dataIndex: "budgetAmount",
      align: "left",
      render: (_, record) => {
        return (
          <Typography.Text>
            {formatCurrency(
              record?.promotionCampaignTier?.maxBudgetPerSeller ?? record?.promotionCampaignTier?.budgetAmount
            )}
          </Typography.Text>
        );
      },
    },
    {
      title: "NGÂN SÁCH KHẢ DỤNG",
      dataIndex: "availableBudgetAmount",
      align: "center",
      render: (_, record) => {
        return (
          <Typography.Text
            style={{
              color: record?.promotionCampaignTier?.availableBudgetAmount === 0 ? "#52C41A" : "#FF4D4F",
              backgroundColor: record?.promotionCampaignTier?.availableBudgetAmount === 0 ? "#EDF9F0" : "#F9E9E9",
              padding: "6px 12px",
              borderRadius: "12px",
            }}
          >
            {formatCurrency(record?.promotionCampaignTier?.availableBudgetAmount)}
          </Typography.Text>
        );
      },
    },
    // Column TRẠNG THÁI chỉ hiển thị khi tab "Tất cả"
    activeTab === "ALL" && {
      title: "TRẠNG THÁI",
      dataIndex: "status",
      align: "center",
      fixed: "right",
      filterDropdown: (
        <FilterMenu
          params={params}
          onFilter={(value) => handleFilterListVoucher("statuses", value)}
          type="checkbox"
          options={[
            { label: "Sắp diễn ra", value: "UPCOMING" },
            { label: "Có hiệu lực", value: "ONGOING" },
            { label: "Hết hiệu lực", value: "EXPIRED" },
            { label: "Hết lượt sử dụng", value: "OUT_OF_USAGE" },
            { label: "Đã vô hiệu", value: "DISABLE" },
          ]}
          defaultValue={params.filters.statuses}
        />
      ),

      render: (_, record) => {
        const statusConfig = getStatusConfig(record.status);
        return (
          <Space>
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: statusConfig.color,
              }}
            />
            <Typography.Text style={{ color: statusConfig.color }}>{statusConfig.text}</Typography.Text>
          </Space>
        );
      },
    },
    {
      title: "HÀNH ĐỘNG",
      dataIndex: "action",
      align: "center",
      width: 100,
      fixed: "right",
      render: (_, record) => {
        const statusActivate = record.statusActivate === "ACTIVATE";
        return (
          <div
            style={{ cursor: "pointer" }}
            onClick={() => {
              setSelectedVoucher(record);
              setConfirmAction(statusActivate ? "DEACTIVATE" : "ACTIVATE");
              setConfirmModalVisible(true);
            }}
          >
            <StateSwitch checked={statusActivate} style={{ pointerEvents: "none" }} />
          </div>
        );
      },
    },
  ].filter(Boolean); // Lọc bỏ các giá trị false (column TRẠNG THÁI khi không phải tab ALL)

  // Hàm đổi tab
  const handleChangeTab = (tab) => {
    setActiveTab(tab);
    setParams({
      filters: {
        promotionCampaignIDs: [promotionCampaignIDs] || [],
        statuses: tab === "ALL" ? [] : [tab],
      },
      pagination: {
        limit: 10,
        offset: 0,
      },
    });
  };

  const handleChangeTable = (pagination) => {
    const { current, pageSize } = pagination;
    let newParams = {
      ...params,
      pagination: { ...params?.pagination, offset: (current - 1) * pageSize },
    };
    setParams(newParams);
  };

  const [searchTerm, setSearchTerm] = useState("");

  const handleSearchVoucher = (e) => {
    setSearchTerm(e.target.value);
  };

  const debouncedValue = useDebounce(searchTerm, 500);

  // Dùng functional update để tránh stale closure và infinite loop
  useEffect(() => {
    setParams((prevParams) => ({
      ...prevParams,
      filters: {
        ...prevParams.filters,
        codes: debouncedValue?.trim().length > 0 ? [debouncedValue?.trim()] : undefined,
      },
      pagination: {
        limit: 10,
        offset: 0,
      },
    }));
  }, [debouncedValue]);

  const handleConfirmToggle = async () => {
    if (!selectedVoucher?.id) return;

    try {
      if (confirmAction === "ACTIVATE") {
        await handleActivateVoucher({ id: selectedVoucher.id });

        notify.success({
          message: "Kích hoạt voucher thành công!",
        });
      } else {
        await handleDeactivateVoucher({ id: selectedVoucher.id });
        notify.success({
          message: "Vô hiệu hóa voucher thành công!",
        });
      }
      refetch?.();
    } catch (error) {
      message.error("Có lỗi xảy ra, vui lòng thử lại!");
    } finally {
      setConfirmModalVisible(false);
      setSelectedVoucher(null);
      setConfirmAction(null);
    }
  };

  const handleCancelToggle = () => {
    setConfirmModalVisible(false);
    setSelectedVoucher(null);
    setConfirmAction(null);
  };

  // Config cho modal xác nhận
  const confirmModalConfig = {
    visible: confirmModalVisible,
    voucher: selectedVoucher,
    action: confirmAction,
    onConfirm: handleConfirmToggle,
    onCancel: handleCancelToggle,
    loading: loadingActivateVoucher || loadingDeactivateVoucher,
  };

  // Config cho modal xem chi tiết đại lý
  const agentModalConfig = {
    visible: agentModalVisible,
    voucherID: selectedVoucherID,
    onClose: () => {
      setAgentModalVisible(false);
      setSelectedVoucherID(null);
    },
  };

  // Config cho modal xem chi tiết sản phẩm
  const productModalConfig = {
    visible: productModalVisible,
    voucherID: selectedVoucherID,
    onClose: () => {
      setProductModalVisible(false);
      setSelectedVoucherID(null);
    },
  };

  const paramsStatus = (status) => {
    return {
      filters: {
        promotionCampaignIDs: [promotionCampaignIDs] || [],
        statuses: status,
      },
    };
  };

  const { data: COUNT_ALL } = useCountVouchers(paramsStatus([]));
  const { data: COUNT_UPCOMING } = useCountVouchers(paramsStatus(["UPCOMING"]));
  const { data: COUNT_ONGOING } = useCountVouchers(paramsStatus(["ONGOING"]));
  const { data: COUNT_EXPIRED } = useCountVouchers(paramsStatus(["EXPIRED"]));
  const { data: COUNT_OUT_OF_USAGE } = useCountVouchers(paramsStatus(["OUT_OF_USAGE"]));
  const { data: COUNT_DISABLE } = useCountVouchers(paramsStatus(["DISABLE"]));

  const dataCount = {
    ALL: COUNT_ALL,
    UPCOMING: COUNT_UPCOMING,
    ONGOING: COUNT_ONGOING,
    EXPIRED: COUNT_EXPIRED,
    OUT_OF_USAGE: COUNT_OUT_OF_USAGE,
    DISABLE: COUNT_DISABLE,
  };

  return {
    loading,
    paginationData,
    columns,
    data: data || [],
    activeTab,
    renderTagsList,
    renderTagUsedQuantity,
    handleChangeTab,
    handleChangeTable,
    handleSearchVoucher,
    confirmModalConfig,
    agentModalConfig,
    productModalConfig,
    dataCount,
  };
}
